import React, { useState, useEffect, useMemo } from 'react';
import './BomMaster.css'; // Leaving this for now, but most styles will be overridden by MUI
import Header from '../../Header';
import {
    Card,
    CardContent,
    CardHeader,
    Grid,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper,
    Autocomplete,
    Box,
    Typography,
    Divider,
    Checkbox,
    Snackbar,
    Alert,
    Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScaleIcon from '@mui/icons-material/Scale';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { local_signage } from '../../Const';

// No mock data needed - all items fetched from API

export const BomMaster = () => {
    // Get auth token from localStorage
    const token = useMemo(() => localStorage.getItem('AuthorizationToken'), []);

    // State
    const [conversionTypes, setConversionTypes] = useState([]);
    const [conversionTypesLoading, setConversionTypesLoading] = useState(true);
    const [conversionType, setConversionType] = useState('');
    const [conversionTypeCode, setConversionTypeCode] = useState('');
    const [parentItems, setParentItems] = useState([]);
    const [parentItemsLoading, setParentItemsLoading] = useState(false);
    const [parentItem, setParentItem] = useState('');
    const [parentItemId, setParentItemId] = useState('');
    const [alternateParentItems, setAlternateParentItems] = useState([]);
    const [alternateParentItemIds, setAlternateParentItemIds] = useState([]);
    const [description, setDescription] = useState('');
    const [headerUnitWeight, setHeaderUnitWeight] = useState('');
    const [childItems, setChildItems] = useState([]);
    const [childItemsLoading, setChildItemsLoading] = useState(false);
    const [childItem, setChildItem] = useState('');
    const [childItemId, setChildItemId] = useState('');
    const [nos, setNos] = useState('');
    const [unitWeight, setUnitWeight] = useState('');
    const [minWeight, setMinWeight] = useState('');
    const [maxWeight, setMaxWeight] = useState('');
    const [byProduct, setByProduct] = useState(false);
    const [mappingRows, setMappingRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Notification State matches MUI Snackbar
    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification({ ...notification, show: false });
    };

    // Fetch Conversion Types from API
    useEffect(() => {
        const fetchConversionTypes = async () => {
            try {
                setConversionTypesLoading(true);
                const response = await fetch(`${local_signage}/api/v1/fetchconversiontypelist`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch conversion types');
                }
                const data = await response.json();
                setConversionTypes(data.filter(item => item.Deleted === 'N'));
            } catch (error) {
                console.error('Error fetching conversion types:', error);
                showNotification('Failed to load conversion types', 'error');
                setConversionTypes([]);
            } finally {
                setConversionTypesLoading(false);
            }
        };

        fetchConversionTypes();
    }, [token]);

    // Fetch Parent Items when conversion type changes
    useEffect(() => {
        const fetchParentItems = async () => {
            if (!conversionTypeCode) {
                setParentItems([]);
                return;
            }

            const selectedType = conversionTypes.find(ct => ct.ConversionTypeCode === conversionTypeCode);
            if (!selectedType || !selectedType.ParentItemFamilyID) {
                setParentItems([]);
                return;
            }

            try {
                setParentItemsLoading(true);
                const response = await fetch(
                    `${local_signage}/api/v1/fetchitembyfamily?ItemFamilyID=${selectedType.ParentItemFamilyID}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch parent items');
                }
                const data = await response.json();
                setParentItems(data.filter(item => item.Deleted === 'N'));
            } catch (error) {
                console.error('Error fetching parent items:', error);
                showNotification('Failed to load parent items', 'error');
                setParentItems([]);
            } finally {
                setParentItemsLoading(false);
            }
        };

        fetchParentItems();
    }, [conversionTypeCode, conversionTypes, token]);

    // Fetch Child Items when conversion type changes
    useEffect(() => {
        const fetchChildItems = async () => {
            if (!conversionTypeCode) {
                setChildItems([]);
                return;
            }

            const selectedType = conversionTypes.find(ct => ct.ConversionTypeCode === conversionTypeCode);
            if (!selectedType || !selectedType.ChildItemFamilyID) {
                setChildItems([]);
                return;
            }

            try {
                setChildItemsLoading(true);
                const response = await fetch(
                    `${local_signage}/api/v1/fetchitembyfamily?ItemFamilyID=${selectedType.ChildItemFamilyID}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch child items');
                }
                const data = await response.json();
                setChildItems(data.filter(item => item.Deleted === 'N'));
            } catch (error) {
                console.error('Error fetching child items:', error);
                showNotification('Failed to load child items', 'error');
                setChildItems([]);
            } finally {
                setChildItemsLoading(false);
            }
        };

        fetchChildItems();
    }, [conversionTypeCode, conversionTypes, token]);

    const isAddButtonDisabled = () => {
        return !childItem || !unitWeight || !minWeight || !maxWeight ||
            parseFloat(minWeight) > parseFloat(maxWeight);
    };

    const handleAddMapping = () => {
        if (isAddButtonDisabled()) {
            showNotification('Please fill all required fields correctly', 'error');
            return;
        }

        const newRow = {
            id: Date.now(),
            partSeq: String(mappingRows.length + 1).padStart(3, '0'),
            childItem,
            childItemId,
            nos: nos === '' ? '' : parseInt(nos),
            unitWeight: parseFloat(unitWeight),
            byProduct,
            minWeight: parseFloat(minWeight),
            maxWeight: parseFloat(maxWeight),
        };

        setMappingRows([...mappingRows, newRow]);
        setChildItem('');
        setNos('');
        setUnitWeight('');
        setMinWeight('');
        setMaxWeight('');
        setByProduct(false);
        showNotification('Item mapping added successfully');
    };

    const handleDeleteRow = (id) => {
        const updatedRows = mappingRows
            .filter(row => row.id !== id)
            .map((row, index) => ({ ...row, partSeq: String(index + 1).padStart(3, '0') }));
        setMappingRows(updatedRows);
        setSelectedRows(selectedRows.filter(rowId => rowId !== id));
        showNotification('Item deleted', 'info');
    };

    const handleDeleteSelected = () => {
        if (selectedRows.length === 0) return;
        const updatedRows = mappingRows
            .filter(row => !selectedRows.includes(row.id))
            .map((row, index) => ({ ...row, partSeq: String(index + 1).padStart(3, '0') }));
        setMappingRows(updatedRows);
        setSelectedRows([]);
        showNotification(`${selectedRows.length} item(s) deleted`, 'info');
    };

    const handleSelectRow = (id) => {
        setSelectedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
    };

    const handleSelectAll = (e) => {
        setSelectedRows(e.target.checked ? mappingRows.map(row => row.id) : []);
    };

    // Submit BOM to API
    const handleSubmitBOM = async () => {
        // Validation
        if (!conversionTypeCode) {
            showNotification('Please select a conversion type', 'error');
            return;
        }
        if (!parentItemId) {
            showNotification('Please select a parent item', 'error');
            return;
        }
        if (!headerUnitWeight) {
            showNotification('Please enter unit weight', 'error');
            return;
        }
        if (mappingRows.length === 0) {
            showNotification('Please add at least one child item', 'error');
            return;
        }

        try {
            setIsSubmitting(true);

            // Construct payload
            const payload = {
                Option: "1",
                BomID: "",
                ItemID: parentItemId,
                ConversionTypeCode: conversionTypeCode,
                Description: description || "",
                UnitWt: parseFloat(headerUnitWeight),
                isBranchSpecific: true,
                CreatedUserCode: localStorage.getItem('userId') || 'ADMIN',
                Det: mappingRows.map(row => ({
                    PartSeq: row.partSeq,
                    PartItemID: row.childItemId || row.childItem, // Use childItemId if available
                    UnitPcs: row.nos === '' ? '' : row.nos,
                    UnitWt: row.unitWeight,
                    isByProduct: "N",
                    MiniWtLimit: row.minWeight,
                    MaxiWtLimit: row.maxWeight,
                    Deleted: "N",
                    DML: "I"
                })),
                Altbomitem: alternateParentItemIds.map(altItemId => ({
                    ItemId: parentItemId,
                    AltItemId: altItemId,
                    DML: "I"
                }))
            };

            const response = await fetch(`${local_signage}/api/v1/insertbomho`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Failed to submit BOM');
            }

            const result = await response.json();
            showNotification('BOM submitted successfully!', 'success');

            // Clear form after successful submission
            setConversionType('');
            setConversionTypeCode('');
            setParentItem('');
            setParentItemId('');
            setAlternateParentItems([]);
            setAlternateParentItemIds([]);
            setDescription('');
            setHeaderUnitWeight('');
            setMappingRows([]);
            setSelectedRows([]);

        } catch (error) {
            console.error('Error submitting BOM:', error);
            showNotification('Failed to submit BOM. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
            <Header />
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <Card sx={{ maxWidth: 1280, width: '100%', borderRadius: 2, boxShadow: 3 }}>
                    <CardHeader
                        title={
                            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: '#004d00' }}>
                                BOM Master
                            </Typography>
                        }
                        sx={{ borderBottom: '3px solid #004d00', pb: 1 }}
                    />
                    <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            {/* Conversion Information Section */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#004d00' }}>
                                    <DescriptionIcon sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Conversion Information</Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sx={{ flexBasis: { md: '20%' }, maxWidth: { md: '20%' } }}>
                                        <Autocomplete
                                            options={conversionTypes}
                                            getOptionLabel={(option) => option.ConversionTypeName || ''}
                                            value={conversionTypes.find(ct => ct.ConversionTypeCode === conversionTypeCode) || null}
                                            onChange={(event, newValue) => {
                                                if (newValue) {
                                                    setConversionTypeCode(newValue.ConversionTypeCode);
                                                    setConversionType(newValue.ConversionTypeName);
                                                    // Clear parent items when conversion type changes
                                                    setParentItem('');
                                                    setParentItemId('');
                                                    setAlternateParentItems([]);
                                                    setAlternateParentItemIds([]);
                                                    // Clear child items when conversion type changes
                                                    setChildItem('');
                                                    setChildItemId('');
                                                } else {
                                                    setConversionTypeCode('');
                                                    setConversionType('');
                                                }
                                            }}
                                            disabled={conversionTypesLoading}
                                            renderInput={(params) => <TextField {...params} label="Conversion Type" variant="outlined" size="small" required />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ flexBasis: { md: '20%' }, maxWidth: { md: '20%' } }}>
                                        <Autocomplete
                                            options={parentItems}
                                            getOptionLabel={(option) => option.ItemName || ''}
                                            value={parentItems.find(item => item.ItemID === parentItemId) || null}
                                            onChange={(event, newValue) => {
                                                setParentItemId(newValue ? newValue.ItemID : '');
                                                setParentItem(newValue ? newValue.ItemName : '');
                                                // Clear alternates when parent changes
                                                setAlternateParentItems([]);
                                                setAlternateParentItemIds([]);
                                            }}
                                            disabled={parentItemsLoading || parentItems.length === 0}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Parent Item"
                                                    placeholder={parentItemsLoading ? 'Loading...' : parentItems.length === 0 ? 'Select conversion type first' : 'Select parent item'}
                                                    variant="outlined"
                                                    size="small"
                                                    required
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ flexBasis: { md: '60%' }, maxWidth: { md: '60%' } }}>
                                        <Autocomplete
                                            multiple
                                            options={parentItems.filter(item => item.ItemID !== parentItemId)}
                                            disableCloseOnSelect
                                            getOptionLabel={(option) => option.ItemName}
                                            value={parentItems.filter(item => alternateParentItemIds.includes(item.ItemID))}
                                            onChange={(event, newValue) => {
                                                setAlternateParentItemIds(newValue.map(item => item.ItemID));
                                                setAlternateParentItems(newValue.map(item => item.ItemName));
                                            }}
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox
                                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                        style={{ marginRight: 8 }}
                                                        checked={selected}
                                                    />
                                                    {option.ItemName}
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Alternate Parent Items"
                                                    placeholder="Select alternate items"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Unit Weight (KG)"
                                            type="number"
                                            value={headerUnitWeight}
                                            onChange={(e) => setHeaderUnitWeight(e.target.value)}
                                            placeholder="0.00"
                                            inputProps={{ step: "0.01", min: "0" }}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            fullWidth
                                            label="Description"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Optional description"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            {/* Item Details Section */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#004d00' }}>
                                    <InventoryIcon sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Item Details</Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            options={childItems}
                                            getOptionLabel={(option) => option.ItemName || ''}
                                            value={childItems.find(item => item.ItemID === childItemId) || null}
                                            onChange={(event, newValue) => {
                                                setChildItemId(newValue ? newValue.ItemID : '');
                                                setChildItem(newValue ? newValue.ItemName : '');
                                            }}
                                            disabled={childItemsLoading || childItems.length === 0}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Child Item"
                                                    placeholder={childItemsLoading ? 'Loading...' : childItems.length === 0 ? 'Select conversion type first' : 'Select child item'}
                                                    variant="outlined"
                                                    size="small"
                                                    required
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Nos (Unit Pcs)"
                                            type="number"
                                            value={nos}
                                            onChange={(e) => setNos(e.target.value)}
                                            placeholder="0"
                                            inputProps={{ step: "1", min: "1" }}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Unit Weight (KG)"
                                            type="number"
                                            value={unitWeight}
                                            onChange={(e) => setUnitWeight(e.target.value)}
                                            placeholder="0.00"
                                            inputProps={{ step: "0.01", min: "0" }}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Weight Limits Section */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#004d00' }}>
                                    <ScaleIcon sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Weight Limits</Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Min Weight (KG)"
                                            type="number"
                                            value={minWeight}
                                            onChange={(e) => setMinWeight(e.target.value)}
                                            placeholder="0.00"
                                            inputProps={{ step: "0.01", min: "0" }}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Weight (KG)"
                                            type="number"
                                            value={maxWeight}
                                            onChange={(e) => setMaxWeight(e.target.value)}
                                            placeholder="0.00"
                                            inputProps={{ step: "0.01", min: "0" }}
                                            variant="outlined"
                                            size="small"
                                            required
                                        />
                                    </Grid>
                                    {/* <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={byProduct}
                                            onChange={(e) => setByProduct(e.target.checked)}
                                            icon={<CheckBoxOutlineBlankIcon />}
                                            checkedIcon={<CheckBoxIcon />}
                                            sx={{
                                                color: '#004d00',
                                                '&.Mui-checked': {
                                                    color: '#004d00',
                                                },
                                            }}
                                        />
                                        <Typography variant="body2">ByProduct</Typography>
                                    </Grid> */}
                                    <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddMapping}
                                            disabled={isAddButtonDisabled()}
                                            sx={{
                                                bgcolor: '#004d00',
                                                '&:hover': { bgcolor: '#003300' },
                                                height: '40px'
                                            }}
                                        >
                                            Add Item
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            {/* Parts List Table */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#004d00' }}>
                                        <SettingsIcon sx={{ mr: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Parts List ({mappingRows.length})</Typography>
                                    </Box>
                                    {selectedRows.length > 0 && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={handleDeleteSelected}
                                            size="small"
                                        >
                                            Delete Selected ({selectedRows.length})
                                        </Button>
                                    )}
                                </Box>

                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" sx={{ bgcolor: '#e8f3e8' }}>
                                                    <Checkbox
                                                        checked={mappingRows.length > 0 && selectedRows.length === mappingRows.length}
                                                        indeterminate={selectedRows.length > 0 && selectedRows.length < mappingRows.length}
                                                        onChange={handleSelectAll}
                                                        size="small"
                                                        sx={{ color: '#004d00', '&.Mui-checked': { color: '#004d00' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Part Seq</TableCell>
                                                <TableCell sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Part Item Name</TableCell>
                                                <TableCell align="right" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Unit Pcs</TableCell>
                                                <TableCell align="right" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Unit Wt (KG)</TableCell>
                                                <TableCell align="center" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Byproduct</TableCell>
                                                <TableCell align="right" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Min Unit Wt</TableCell>
                                                <TableCell align="right" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Max Unit Wt</TableCell>
                                                <TableCell align="center" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {mappingRows.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={9} align="center" sx={{ py: 3, fontStyle: 'italic', color: '#666' }}>
                                                        No item mappings added yet. Use the form above to add items.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                mappingRows.map((row) => (
                                                    <TableRow key={row.id} hover selected={selectedRows.includes(row.id)}>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedRows.includes(row.id)}
                                                                onChange={() => handleSelectRow(row.id)}
                                                                size="small"
                                                                sx={{ color: '#004d00', '&.Mui-checked': { color: '#004d00' } }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{row.partSeq}</TableCell>
                                                        <TableCell>{row.childItem}</TableCell>
                                                        <TableCell align="right">{row.nos}</TableCell>
                                                        <TableCell align="right">{row.unitWeight.toFixed(2)}</TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={row.byProduct ? 'YES' : 'NO'}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: row.byProduct ? '#004d00' : '#e0e0e0',
                                                                    color: row.byProduct ? '#fff' : '#666',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.7em',
                                                                    height: '24px'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">{row.minWeight.toFixed(2)}</TableCell>
                                                        <TableCell align="right">{row.maxWeight.toFixed(2)}</TableCell>
                                                        <TableCell align="center">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteRow(row.id)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>

                            {/* Submit Button */}
                            {mappingRows.length > 0 && (
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={isSubmitting ? null : <SaveIcon />}
                                        onClick={handleSubmitBOM}
                                        disabled={isSubmitting}
                                        sx={{
                                            bgcolor: '#004d00',
                                            '&:hover': { bgcolor: '#003300' },
                                            minWidth: 140
                                        }}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit BOM'}
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            </Box>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.show}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={notification.type === '' ? 'info' : notification.type}
                    sx={{ width: '100%', boxShadow: 3 }}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BomMaster;
